import type { Room } from "building-types/rooms/roomTypes";
import { RoomEnclosure } from "building-types/rooms/roomTypesEnum";
import type { AreaRule } from "building-types/rooms/takeoffTypes";
import {
  FloorAreaTakeoffType,
  RoomEnclosureSimple,
  RoomSelectorType,
  BorderPosition,
} from "building-types/rooms/takeoffTypes";
import type { ViewFilter } from "building-types/views/viewFilterTypes";
import { toTitleCase } from "common/utils/strings";

export function floorAreaTakeoffName(takeoffType: string): string {
  switch (takeoffType) {
    case FloorAreaTakeoffType.CONDITIONED_FLOOR_AREA:
      return "CONDITIONED AREA";
    case FloorAreaTakeoffType.UNCONDITIONED_FLOOR_AREA:
      return "UNCONDITIONED AREA";
    case FloorAreaTakeoffType.FRONT_PORCH_AREA:
      return "FRONT PORCH AND PORTICO AREA";
    case FloorAreaTakeoffType.COVERED_PATIO_AREA:
      return "COVERED PATIO AREA";
    case FloorAreaTakeoffType.UNCOVERED_PATIO_AREA:
      return "PATIO AREA";
    case FloorAreaTakeoffType.REAR_PORCH_AREA:
      return "REAR PORCH AREA";
    default:
      return takeoffType.replace(/_/g, " ");
  }
}

export function floorAreaTakeoffViewFilterName(viewFilter: keyof ViewFilter): string {
  switch (viewFilter) {
    case "floorAreasFrontPorch":
      return "Front Porch & Portico";
    case "floorAreasCoveredPatio":
      return "Covered Patio";
    case "floorAreasRearPorch":
      return "Rear Porch";
    case "floorAreasUncoveredPatio":
      return "Uncovered Patio";
    case "floorMaterialAreas":
      return "Floor Material Areas";
    case "measurements":
      return "Measurements";
    default:
      return toTitleCase(viewFilter.replace("floorAreas", "").trimEnd());
  }
}
function roomEnclosureToRoomEnclosureSimple(enclosure: RoomEnclosure): RoomEnclosureSimple {
  switch (enclosure) {
    case RoomEnclosure.UNCONDITIONED:
      return RoomEnclosureSimple.UNCONDITIONED;
    case RoomEnclosure.CONDITIONED:
      return RoomEnclosureSimple.CONDITIONED;
    case RoomEnclosure.EXTERIOR:
    case RoomEnclosure.NONE:
    case RoomEnclosure.OPEN:
      return RoomEnclosureSimple.NONE;
  }
}
const TakeoffTypeToViewFilterKey: {
  [key in FloorAreaTakeoffType]?: keyof ViewFilter;
} = {
  [FloorAreaTakeoffType.CONDITIONED_FLOOR_AREA]: "floorAreasConditioned",
  [FloorAreaTakeoffType.UNCONDITIONED_FLOOR_AREA]: "floorAreasUnconditioned",
  [FloorAreaTakeoffType.TOTAL_GARAGE_AREA]: "floorAreasGarage",
  [FloorAreaTakeoffType.FRONT_PORCH_AREA]: "floorAreasFrontPorch",
  [FloorAreaTakeoffType.COVERED_PATIO_AREA]: "floorAreasCoveredPatio",
  [FloorAreaTakeoffType.UNCOVERED_PATIO_AREA]: "floorAreasUncoveredPatio",
  [FloorAreaTakeoffType.REAR_PORCH_AREA]: "floorAreasRearPorch",
};

export function takeoffTypeToViewFilterKey(
  takeoffType: FloorAreaTakeoffType,
  defaultViewFilter: keyof ViewFilter = "floorAreas",
): keyof ViewFilter {
  return TakeoffTypeToViewFilterKey[takeoffType] ?? defaultViewFilter;
}

export function isRoomSelected(room: Room, selectors: RoomSelectorType[]): boolean {
  if (!selectors) {
    return false;
  }
  const roomEnclosure = roomEnclosureToRoomEnclosureSimple(room.attributes.roomEnclosure);
  for (const selector of selectors) {
    switch (selector) {
      case RoomSelectorType.CONDITIONED: {
        if (roomEnclosure === RoomEnclosureSimple.CONDITIONED) {
          return true;
        }
        break;
      }
      case RoomSelectorType.UNCONDITIONED: {
        if (roomEnclosure === RoomEnclosureSimple.UNCONDITIONED) {
          return true;
        }
        break;
      }
      case RoomSelectorType.NOT_CONDITIONED_OR_UNCONDITIONED: {
        if (roomEnclosure === RoomEnclosureSimple.NONE) {
          return true;
        }
        break;
      }
    }
  }

  return false;
}

export function areaRuleNew(newRule: AreaRule): AreaRule {
  if (!newRule.otherRoomBorderPosition) {
    newRule = { ...newRule, otherRoomBorderPosition: BorderPosition.CENTERLINE };
  }
  if (!newRule.exteriorRoomBorderPosition) {
    newRule = { ...newRule, exteriorRoomBorderPosition: BorderPosition.FRAMING_OUTSIDE };
  }

  return newRule;
}
